import React, { useEffect, useRef, useState } from "react";
import { useToast } from "../context/ToastContext";
import "./ToolPage.css";
import flaskApi from "../services/flaskApi";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import { extractErrorMessage } from "../utils/errorUtils";
import ToolInfoModal from "../components/common/ToolInfoModal";
import ToolPageLayout from "./shared/ToolPageLayout";
import ToolProgressPanel from "./shared/ToolProgressPanel";
import ToolJobsSection from "./shared/ToolJobsSection";
import useJobsPagination from "./shared/useJobsPagination";
import AmazonSourceSelector, {
  AMAZON_MODES,
  buildAmazonSourceFormData,
} from "./shared/AmazonSourceSelector";

const STATUS_DONE = "done";
const STATUS_FAILED = "failed";

const sourceLabel = (j) =>
  j.input_mode === "vendor"
    ? `Vendor: ${j.vendor}`
    : j.input_mode === "sku"
    ? `Parent SKU: ${j.parent_sku}`
    : `File: ${j.input_filename || "—"}`;

function AmazonPricingBuyBoxTool() {
  const [mode, setMode] = useState(AMAZON_MODES.VENDOR);
  const [file, setFile] = useState(null);
  const [vendor, setVendor] = useState("");
  const [parentSku, setParentSku] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  const { addToast } = useToast();
  const [showInfoModal, setShowInfoModal] = useState(false);

  const {
    jobs,
    page,
    totalPages,
    totalFinished,
    setPage,
    refetch: refetchJobs,
  } = useJobsPagination({
    endpoint: "/amazon/pricing-report/jobs",
    cacheKey: "amazonPricingBuyBox.jobs",
    pageSize: 10,
  });

  const toolInfo = {
    toolName: "Amazon Pricing & Buy Box",
    description:
      "For a vendor, an uploaded parent-SKU list, or a single parent SKU, pull Amazon competitive pricing, the Buy Box price, whether we hold the Buy Box, and offer counts. Shopify price shown for reference. Read-only.",
    steps: [
      "Choose the products: a Shopify vendor, an uploaded parent-SKU file, or a single parent SKU.",
      "Click Run. Shopify resolves the variants; Amazon pricing is pulled per SKU/ASIN.",
      "Download the XLSX — 'Pricing' has Buy Box price, whether we win it, offer counts, and the Shopify price.",
    ],
    templates: [],
  };

  const guideTips = [
    "Read-only — never changes prices on Amazon.",
    "Competitive pricing is batched (20 SKUs/Amazon call); Buy Box detail is fetched per ASIN.",
    "'We Win Buy Box = Yes' means our offer is the featured offer for that ASIN right now.",
  ];

  const previousStatusRef = useRef({});
  useEffect(() => {
    const previous = previousStatusRef.current;
    jobs.forEach((j) => {
      const prevStatus = previous[j.job_id];
      if (prevStatus && prevStatus !== j.status) {
        if (j.status === STATUS_DONE) addToast(`Report ${j.job_id} complete.`, "success");
        else if (j.status === STATUS_FAILED)
          addToast(`Report ${j.job_id} failed: ${j.error || "unknown error"}`, "error");
      }
    });
    previousStatusRef.current = Object.fromEntries(jobs.map((j) => [j.job_id, j.status]));
  }, [jobs, addToast]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    let formData;
    try {
      formData = buildAmazonSourceFormData({ mode, file, vendor, parentSku });
    } catch (e) {
      addToast(e.message, "error");
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      const response = await flaskApi.post("/amazon/pricing-report", formData, {
        onUploadProgress: (e) =>
          setUploadProgress(Math.round((e.loaded * 100) / (e.total || 1))),
      });
      const jobId = response.data?.job_id;
      if (!jobId) {
        addToast("Server did not return a job ID.", "error");
        return;
      }
      addToast(`Report ${jobId} queued. Tracking progress…`, "info");
      if (page !== 1) await setPage(1);
      else await refetchJobs();
      setFile(null);
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to start report.");
      addToast(message, "error");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownload = async (job) => {
    if (!job?.job_id) return;
    setDownloadingJobId(job.job_id);
    try {
      const response = await flaskApi.get(
        `/amazon/pricing-report/jobs/${job.job_id}/download`,
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const cd =
        response.headers["content-disposition"] || response.headers["Content-Disposition"];
      link.setAttribute(
        "download",
        extractFilenameFromHeader(cd) || job.result_filename || "amazon_pricing_buybox.xlsx"
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      addToast("Report downloaded.", "success");
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to download report.");
      addToast(message, "error");
    } finally {
      setDownloadingJobId(null);
    }
  };

  return (
    <>
      <ToolPageLayout
        splitLayout
        title="Amazon Pricing & Buy Box"
        subtitle={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
        tips={guideTips}
        onOpenInfo={() => setShowInfoModal(true)}
      >
        <form onSubmit={handleSubmit} className="tool-form">
          <div className="tool-form-main">
            <AmazonSourceSelector
              mode={mode}
              setMode={setMode}
              file={file}
              setFile={setFile}
              vendor={vendor}
              setVendor={setVendor}
              parentSku={parentSku}
              setParentSku={setParentSku}
              disabled={uploading}
            />

            <div className="tool-action-stack">
              <button type="submit" className="tool-btn-primary" disabled={uploading}>
                {uploading ? "Starting…" : "Run Report"}
              </button>
              <ToolProgressPanel
                loading={uploading}
                uploadProgress={uploadProgress}
                uploadMessage="Uploading…"
                processingMessage="Queueing job…"
              />
            </div>
          </div>
        </form>

        <ToolJobsSection
          title="Your Pricing Jobs"
          emptyText="No pricing jobs yet. Choose a source and click Run Report."
          jobs={jobs}
          page={page}
          totalPages={totalPages}
          totalFinished={totalFinished}
          onPageChange={setPage}
          renderJob={(j) => {
            const progress = j.progress || {};
            const total = Number(progress.total) || 0;
            const processed = Number(progress.processed) || 0;
            const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
            return (
              <div key={j.job_id} className="tool-job-status">
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Job ID</span>
                  <span className="tool-job-value">{j.job_id}</span>
                </div>
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Source</span>
                  <span className="tool-job-value">{sourceLabel(j)}</span>
                </div>
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Status</span>
                  <span className={`tool-job-value tool-job-status-${j.status}`}>{j.status}</span>
                </div>
                {total > 0 ? (
                  <>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Progress</span>
                      <span className="tool-job-value">
                        {processed.toLocaleString()} / {total.toLocaleString()} ({pct}%)
                      </span>
                    </div>
                    <div className="progress-bar-container">
                      <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
                    </div>
                  </>
                ) : null}
                <div className="tool-job-counts">
                  <span>Buy Box held: {progress.buy_box_wins || 0}</span>
                </div>
                {j.status === STATUS_FAILED && j.error ? (
                  <div className="tool-job-error">Error: {j.error}</div>
                ) : null}
                {j.status === STATUS_DONE ? (
                  <button
                    type="button"
                    className="tool-btn-primary"
                    onClick={() => handleDownload(j)}
                    disabled={downloadingJobId === j.job_id}
                  >
                    {downloadingJobId === j.job_id ? "Downloading…" : "Download Report"}
                  </button>
                ) : null}
              </div>
            );
          }}
        />
      </ToolPageLayout>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}

export default AmazonPricingBuyBoxTool;
